/**
 * Shared by every layer of the auto-update feature (poller, lazyWithRecovery, the global
 * unhandledrejection net and the error-boundary fallback). It compares the version compiled
 * into this bundle with the one deployed in version.json, and reloads with a loop guard.
 */

import { BROADCAST_EVENT } from "../hooks/useBrowserTabEvent";

export const VERSION_STORAGE_KEY = "app_version";
export const RELOAD_MARKER_KEY = "app_build_reload_marker";
export const RELOAD_COOLDOWN_MS = 60_000;
/** Reload target used when a chunk is gone but version.json shows no new version. */
export const STALE_CHUNK_TARGET = "stale-chunk";

/**
 * localStorage keys that survive an update. A deploy is not an auth event: dropping
 * `persist:root` would sign out everyone who is logged in at release time.
 */
export const PRESERVED_STORAGE_KEYS: readonly string[] = [
  "persist:root", // redux-persist, key 'root' in src/store/store.ts
  "kt_theme_mode_value", // ThemeModeProvider.tsx themeModelSKey
  "kt_theme_mode_menu", // ThemeModeProvider.tsx themeMenuModeLSKey
  import.meta.env.VITE_APP_I18N_CONFIG_KEY || "i18nConfig", // Metronici18n.tsx
  import.meta.env.VITE_APP_BASE_LAYOUT_CONFIG_KEY || "LayoutConfig", // LayoutSetup.ts
  "redirectUrl", // utils/session.ts
  "redirectUserFingerprint", // utils/session.ts
  // Cross-tab fallback keys: removing one fires a storage event that other tabs treat as
  // that event (e.g. a logout) on browsers without BroadcastChannel.
  ...Object.values(BROADCAST_EVENT),
  VERSION_STORAGE_KEY,
];

export const BUILD_VERSION = typeof __APP_BUILD_VERSION__ === "string" ? __APP_BUILD_VERSION__ : "";

/** Lower-cased substrings browsers and bundlers use for a chunk that can't be loaded. */
const STALE_CHUNK_MESSAGES = [
  "failed to fetch dynamically imported module", // Chromium
  "error loading dynamically imported module", // Firefox
  "importing a module script failed", // Safari
  "unable to preload css", // Vite CSS preload
  "expected a javascript-or-wasm module script", // MIME refusal
  "strict mime type checking",
  "is not a valid javascript mime type",
  "loading chunk", // webpack
  "loading css chunk", // webpack
];

/** Indirection so tests can observe the reload; jsdom's location.reload can't be spied on. */
export const browser = {
  reload: (): void => window.location.reload(),
};

interface ReloadMarker {
  target: string;
  at: number;
}

const readStorage = (storage: Storage, key: string): string | null => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (storage: Storage, key: string, value: string): void => {
  try {
    storage.setItem(key, value);
  } catch {
    // Storage can be full or blocked; the reload still has to happen.
  }
};

const getErrorText = (error: unknown): string => {
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "message" in error) return String(error.message);
  return "";
};

export const isStaleChunkError = (error: unknown): boolean => {
  if (!error) return false;
  if (error instanceof Error && error.name === "ChunkLoadError") return true;
  const message = getErrorText(error).toLowerCase();
  return STALE_CHUNK_MESSAGES.some((pattern) => message.includes(pattern));
};

/**
 * Returns null when version.json can't be trusted: a non-OK response, a network failure, or
 * a body without a string version (the SPA fallback answering with index.html).
 */
export const fetchDeployedVersion = async (): Promise<string | null> => {
  try {
    const response = await fetch(`${import.meta.env.BASE_URL}version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    const version = body && typeof body === "object" ? (body as { version?: unknown }).version : undefined;
    return typeof version === "string" && version !== "" ? version : null;
  } catch {
    return null;
  }
};

/** The compiled-in stamp wins; the stored copy is only a fallback for unstamped bundles. */
export const getRunningVersion = (): string =>
  BUILD_VERSION || readStorage(localStorage, VERSION_STORAGE_KEY) || "";

/** The deployed version when it differs from the running one, otherwise null. */
export const findNewDeployedVersion = async (): Promise<string | null> => {
  const deployed = await fetchDeployedVersion();
  if (!deployed) return null;

  const running = getRunningVersion();
  if (!running || running === deployed) {
    writeStorage(localStorage, VERSION_STORAGE_KEY, deployed);
    return null;
  }
  return deployed;
};

const readReloadMarker = (): ReloadMarker | null => {
  const raw = readStorage(sessionStorage, RELOAD_MARKER_KEY);
  if (!raw) return null;
  try {
    const marker: unknown = JSON.parse(raw);
    if (!marker || typeof marker !== "object") return null;
    const { target, at } = marker as Partial<ReloadMarker>;
    return typeof target === "string" && typeof at === "number" ? { target, at } : null;
  } catch {
    return null;
  }
};

/** False when this tab already reloaded for the same target within the cooldown. */
export const canReloadFor = (target: string): boolean => {
  const marker = readReloadMarker();
  if (!marker || marker.target !== target) return true;
  return Date.now() - marker.at >= RELOAD_COOLDOWN_MS;
};

const clearCacheStorage = async (): Promise<void> => {
  if (typeof caches === "undefined") return;
  const names = await caches.keys();
  await Promise.all(names.map((name) => caches.delete(name)));
};

const unregisterServiceWorkers = async (): Promise<void> => {
  if (!("serviceWorker" in navigator) || !navigator.serviceWorker) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));
};

const clearLocalStorageExceptPreserved = (): void => {
  // Copy the keys first: removing entries while walking the live index skips some of them.
  const keys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index)).filter(
    (key): key is string => key !== null && !PRESERVED_STORAGE_KEYS.includes(key)
  );
  keys.forEach((key) => localStorage.removeItem(key));
};

const evictOldBuildState = async (): Promise<void> => {
  const settled = await Promise.allSettled([clearCacheStorage(), unregisterServiceWorkers()]);
  settled
    .filter((result): result is PromiseRejectedResult => result.status === "rejected")
    .forEach((result) => console.warn("[app-version] cache eviction step failed", result.reason));
  try {
    sessionStorage.clear();
    clearLocalStorageExceptPreserved();
  } catch (error) {
    console.warn("[app-version] storage eviction failed", error);
  }
};

let pendingReload: Promise<boolean> | null = null;

const performReload = async (target: string): Promise<boolean> => {
  if (!canReloadFor(target)) return false;

  await evictOldBuildState();
  if (target !== STALE_CHUNK_TARGET) writeStorage(localStorage, VERSION_STORAGE_KEY, target);
  const marker: ReloadMarker = { target, at: Date.now() };
  writeStorage(sessionStorage, RELOAD_MARKER_KEY, JSON.stringify(marker));
  browser.reload();
  return true;
};

/**
 * Evicts the old build's caches and reloads. Returns false when the loop guard refuses, so
 * the caller can fall back (keep the app usable, or show the real error).
 */
export const reloadForNewBuild = (target: string): Promise<boolean> => {
  // The poller, the lazy loader and the global net can all fire at once for one deploy.
  if (!pendingReload) {
    pendingReload = performReload(target).finally(() => {
      pendingReload = null;
    });
  }
  return pendingReload;
};

export const reloadIfDeployedVersionChanged = async (): Promise<boolean> => {
  const next = await findNewDeployedVersion();
  return next ? reloadForNewBuild(next) : false;
};

/** Confirmed deploy first; otherwise the chunk is gone anyway, so reload with the sentinel. */
export const recoverFromStaleBuild = async (): Promise<boolean> =>
  (await reloadIfDeployedVersionChanged()) || reloadForNewBuild(STALE_CHUNK_TARGET);
